// ── Vec3 type ────────────────────────────────────────────────────────────────

export interface Vec3 {
  x: number;
  y: number;
  z: number;
}

// ── Distance functions ───────────────────────────────────────────────────────

export function vec3Distance(a: Vec3, b: Vec3): number {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  const dz = a.z - b.z;
  return Math.sqrt(dx * dx + dy * dy + dz * dz);
}

export function vec3DistanceSq(a: Vec3, b: Vec3): number {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  const dz = a.z - b.z;
  return dx * dx + dy * dy + dz * dz;
}

// ── Interpolation ────────────────────────────────────────────────────────────

export function vec3Lerp(a: Vec3, b: Vec3, t: number): Vec3 {
  return {
    x: a.x + (b.x - a.x) * t,
    y: a.y + (b.y - a.y) * t,
    z: a.z + (b.z - a.z) * t,
  };
}

// ── Arithmetic ───────────────────────────────────────────────────────────────

export function vec3Add(a: Vec3, b: Vec3): Vec3 {
  return { x: a.x + b.x, y: a.y + b.y, z: a.z + b.z };
}

export function vec3Sub(a: Vec3, b: Vec3): Vec3 {
  return { x: a.x - b.x, y: a.y - b.y, z: a.z - b.z };
}

export function vec3Scale(v: Vec3, s: number): Vec3 {
  return { x: v.x * s, y: v.y * s, z: v.z * s };
}

// ── Normalization and magnitude ──────────────────────────────────────────────

export function vec3Normalize(v: Vec3): Vec3 {
  const len = Math.sqrt(v.x * v.x + v.y * v.y + v.z * v.z);
  if (len === 0) {
    return { x: 0, y: 0, z: 0 };
  }
  return { x: v.x / len, y: v.y / len, z: v.z / len };
}

export function vec3Length(v: Vec3): number {
  return Math.sqrt(v.x * v.x + v.y * v.y + v.z * v.z);
}

/**
 * Unit direction `from → to` and its length. Returns null when the two
 * points overlap (direction undefined). Single source of truth for damage
 * direction, knockback launch, and falloff distance.
 */
export function unitDirection(
  from: Vec3,
  to: Vec3,
): { x: number; y: number; z: number; length: number } | null {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const dz = to.z - from.z;
  const lenSq = dx * dx + dy * dy + dz * dz;
  if (lenSq < 1e-12) return null;
  const length = Math.sqrt(lenSq);
  const inv = 1 / length;
  return { x: dx * inv, y: dy * inv, z: dz * inv, length };
}

// ── Products ─────────────────────────────────────────────────────────────────

export function vec3Cross(a: Vec3, b: Vec3): Vec3 {
  return {
    x: a.y * b.z - a.z * b.y,
    y: a.z * b.x - a.x * b.z,
    z: a.x * b.y - a.y * b.x,
  };
}

export function vec3Dot(a: Vec3, b: Vec3): number {
  return a.x * b.x + a.y * b.y + a.z * b.z;
}

// ── General utilities ────────────────────────────────────────────────────────

export function clamp(v: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, v));
}

/**
 * Frame-rate-independent exponential decay factor for `lerp(current, target, t)`.
 * `rate` is the inverse time-constant in s^-1; `dt` is the frame step in seconds.
 * Higher `rate` = snappier convergence.
 */
export function expDecay(rate: number, dt: number): number {
  return 1 - Math.exp(-rate * dt);
}

export function generateId(): string {
  return Math.random().toString(36).substring(2, 10);
}

export function randomInRange(min: number, max: number): number {
  return min + Math.random() * (max - min);
}

export { DRONE_STATS, DroneType, Tier } from './types';
import { DRONE_STATS, DroneType, Tier } from './types';
import type { DroneStats } from './types';
import { LOADOUT_SLOTS, DEFAULT_LOADOUT } from './constants';

/** Safe DRONE_STATS lookup — returns undefined for invalid drone type. */
export function getDroneStats(droneType: number): DroneStats | undefined {
  return DRONE_STATS[droneType as DroneType];
}

/**
 * Validate a client-supplied loadout array. Returns a sanitized
 * `LOADOUT_SLOTS`-length tuple of Common-tier `DroneType` indices, or
 * `DEFAULT_LOADOUT` if `input` is missing, not an array of the right length,
 * or contains any non-Common / unknown drone type. Server is the source of
 * truth — clients can lie.
 */
export function sanitizeLoadout(input: unknown): number[] {
  if (!Array.isArray(input) || input.length !== LOADOUT_SLOTS) {
    return [...DEFAULT_LOADOUT];
  }
  const out: number[] = [];
  for (const v of input) {
    if (typeof v !== 'number' || !Number.isInteger(v)) return [...DEFAULT_LOADOUT];
    const stats = DRONE_STATS[v as DroneType];
    if (!stats || stats.tier !== Tier.Common) return [...DEFAULT_LOADOUT];
    out.push(v);
  }
  return out;
}

export function randomVec3InBounds(
  minX: number,
  maxX: number,
  minY: number,
  maxY: number,
  minZ: number,
  maxZ: number
): Vec3 {
  return {
    x: randomInRange(minX, maxX),
    y: randomInRange(minY, maxY),
    z: randomInRange(minZ, maxZ),
  };
}
